import { ArrowDown } from 'lucide-react'
import { CopyField } from '@/components/sv/copy-field'
import { Muted } from '@/components/sv/typographies'
import { getOciMigrationTarget } from '@/config/oci-migration'
import { GoogleCloudLogo } from '@/logos/googlecloud'
import { OciLogo } from '@/logos/oci'
import type { SvlOciMigrationQuery } from '@/components/layout/oci-migration-notice/queries.types.gen'
import type { ReactNode } from 'react'

type GcpCluster = NonNullable<
  SvlOciMigrationQuery['svlOciMigration']
>['gcpClusters'][number]

type EgressIpMappingProps = {
  gcpClusters: GcpCluster[]
}

type ProviderLineProps = {
  logo: ReactNode
  provider: string
  cluster?: string
}

const ProviderLine = ({ logo, provider, cluster }: ProviderLineProps) => (
  <Muted className="flex items-center gap-1.5 text-xs">
    {logo}
    <span>{provider}</span>
    {cluster ? (
      <>
        <span aria-hidden="true">·</span>
        <span className="font-mono">{cluster}</span>
      </>
    ) : null}
  </Muted>
)

const stripRegionSuffix = (displayName: string) =>
  displayName.replace(/\s*\([^)]*\)\s*$/, '')

const EgressIpMappingRow = ({ cluster }: { cluster: GcpCluster }) => {
  const target = getOciMigrationTarget(cluster.name)
  const currentLabel = cluster.displayName
    ? stripRegionSuffix(cluster.displayName)
    : cluster.location

  return (
    <li className="flex flex-col gap-2 rounded-lg border border-border p-3">
      <div className="flex flex-col gap-1 text-sm">
        <span className="flex flex-col gap-0.5">
          <span className="font-medium">{currentLabel}</span>
          <ProviderLine
            logo={<GoogleCloudLogo aria-hidden="true" className="size-3.5" />}
            provider="Google Cloud"
            cluster={cluster.location}
          />
        </span>
        <ArrowDown
          aria-hidden="true"
          className="size-4 shrink-0 text-muted-foreground"
        />
        <span className="flex flex-col gap-0.5">
          <span className="font-medium">
            {target?.label ?? 'Location to be announced'}
          </span>
          <ProviderLine
            logo={<OciLogo aria-hidden="true" className="h-2.5 w-auto" />}
            provider="Oracle Cloud"
            cluster={target?.okeCluster}
          />
        </span>
      </div>
      {target ? (
        <div className="flex flex-wrap items-center gap-2">
          <Muted className="text-xs">New app egress IP</Muted>
          <CopyField value={target.appEgressIp} size="sm" />
        </div>
      ) : (
        <Muted className="text-xs">
          The new egress IP for this location will be announced before your wave
          starts.
        </Muted>
      )}
    </li>
  )
}

export const EgressIpMapping = ({ gcpClusters }: EgressIpMappingProps) => {
  if (gcpClusters.length === 0) {
    return (
      <Muted as="p" className="text-xs">
        We couldn't find any Google Cloud locations in use for this company.
      </Muted>
    )
  }

  return (
    <ul className="flex flex-col gap-2">
      {gcpClusters.map((cluster) => (
        <EgressIpMappingRow key={cluster.id} cluster={cluster} />
      ))}
    </ul>
  )
}
